import { createReducer } from '@reduxjs/toolkit'

import type {
  TDataRerunDetailDto,
  TDataRerunSummaryDto,
  TRerunCandidatesDto
} from '@homepal/api-contract'

import * as actions from 'redux/actions/internal/DataReruns/actions'
import * as authenticationActions from 'redux/actions/Authentication/actions'
import { EReduxState } from 'redux/utils/State'

export interface DataRerunsReducerType {
  candidates: TRerunCandidatesDto | null
  candidatesFetching: boolean
  creating: boolean
  cancelling: boolean
  retrying: boolean
  /** Scope of the latest candidates request, null = all customers. */
  requestedCustomerId: string | null
  /** Newest first, null until fetched. */
  reruns: TDataRerunSummaryDto[] | null
  rerunsFetchStatus: EReduxState
  /** Reruns that have not finished, newest first. */
  activeReruns: TDataRerunSummaryDto[]
  activeRerunsFetchStatus: EReduxState
  selectedRerun: TDataRerunDetailDto | null
  /** Status of the latest detail request. */
  selectedRerunFetchStatus: EReduxState
  /** Id of the latest detail request. */
  requestedRerunId: string | null
}

const INITIAL_STATE: DataRerunsReducerType = {
  candidates: null,
  candidatesFetching: false,
  creating: false,
  cancelling: false,
  retrying: false,
  requestedCustomerId: null,
  reruns: null,
  rerunsFetchStatus: EReduxState.IDLE,
  activeReruns: [],
  activeRerunsFetchStatus: EReduxState.IDLE,
  selectedRerun: null,
  selectedRerunFetchStatus: EReduxState.IDLE,
  requestedRerunId: null
}

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getCandidates, (state, action) => {
      state.candidates = null
      state.candidatesFetching = true
      state.requestedCustomerId = action.payload.customerId ?? null
    })
    .addCase(actions.getCandidatesSuccess, (state, action) => {
      const { payload } = action

      // drops a response for a scope that is no longer requested
      if ((payload.customerId ?? null) !== state.requestedCustomerId) {
        return
      }

      state.candidates = payload.data
      state.candidatesFetching = false
    })
    .addCase(actions.getCandidatesFailed, (state, action) => {
      if ((action.payload.customerId ?? null) !== state.requestedCustomerId) {
        return
      }

      state.candidatesFetching = false
    })
    .addCase(actions.create, (state) => {
      state.creating = true
    })
    .addCase(actions.createSuccess, (state) => {
      state.creating = false
    })
    .addCase(actions.createFailed, (state) => {
      state.creating = false
    })
    .addCase(actions.cancel, (state) => {
      state.cancelling = true
    })
    .addCase(actions.cancelSuccess, (state) => {
      state.cancelling = false
    })
    .addCase(actions.cancelFailed, (state) => {
      state.cancelling = false
    })
    .addCase(actions.retryFailed, (state) => {
      state.retrying = true
    })
    .addCase(actions.retryFailedSuccess, (state) => {
      state.retrying = false
    })
    .addCase(actions.retryFailedFailed, (state) => {
      state.retrying = false
    })
    .addCase(actions.getReruns, (state) => {
      state.rerunsFetchStatus = EReduxState.LOADING
    })
    .addCase(actions.getRerunsSuccess, (state, action) => {
      state.reruns = action.payload
      state.rerunsFetchStatus = EReduxState.SUCCESS
    })
    .addCase(actions.getRerunsFailed, (state) => {
      state.rerunsFetchStatus = EReduxState.ERROR
    })
    .addCase(actions.getActiveReruns, (state) => {
      state.activeRerunsFetchStatus = EReduxState.LOADING
    })
    .addCase(actions.getActiveRerunsSuccess, (state, action) => {
      state.activeReruns = action.payload
      state.activeRerunsFetchStatus = EReduxState.SUCCESS
    })
    .addCase(actions.getActiveRerunsFailed, (state) => {
      state.activeRerunsFetchStatus = EReduxState.ERROR
    })
    .addCase(actions.getRerun, (state, action) => {
      // keeps the shown rerun while the same one is refetched
      if (state.selectedRerun?.id !== action.payload.id) {
        state.selectedRerun = null
      }

      state.requestedRerunId = action.payload.id
      state.selectedRerunFetchStatus = EReduxState.LOADING
    })
    .addCase(actions.getRerunSuccess, (state, action) => {
      // drops a response for a rerun that is no longer requested
      if (action.payload.id !== state.requestedRerunId) {
        return
      }

      state.selectedRerun = action.payload
      state.selectedRerunFetchStatus = EReduxState.SUCCESS
    })
    .addCase(actions.getRerunFailed, (state, action) => {
      if (action.payload.id !== state.requestedRerunId) {
        return
      }

      state.selectedRerunFetchStatus = EReduxState.ERROR
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
